import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { activeReservations, conversionFunnel, monthlyReport, priceChanges, soldOrders } from "../api/endpoints";
import ProductPhoto from "../components/ProductPhoto";
import { HandBlot } from "../components/Hand";
import type { Order, PriceChange } from "../types";
import { CATEGORY_LABELS, categoryLabel } from "../types";

type Funnel = Awaited<ReturnType<typeof conversionFunnel>>;

const RESERVATION_LABELS = {
  free: "Бесплатно · 24ч",
  week: "Неделя · залог 5%",
  month: "Месяц · залог 10%",
};

const RESERVATION_COLORS = {
  free:  { bg: "rgba(34,139,34,0.12)",  text: "#1a7f1a" },
  week:  { bg: "rgba(180,140,0,0.12)",  text: "#8a6900" },
  month: { bg: "rgba(176,0,32,0.11)",   text: "#b00020" },
};

function ProductOrderCard({ order }: { order: Order }) {
  const p = order.product;
  const rt = order.reservation_type;
  const color = rt ? RESERVATION_COLORS[rt] : null;

  return (
    <Link to={`/product/${p.id}`} style={{ display: "flex", gap: 12, padding: 12, background: "var(--secondary-bg)", borderRadius: 14, marginBottom: 8 }}>
      <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={64} variant="thumb" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14 }}>
          {p.title ?? categoryLabel(p.category)}
        </p>
        <p className="micro" style={{ margin: "0 0 5px", color: "var(--hint)" }}>
          {p.size} · #{p.sku}
        </p>
        <p style={{ margin: "0 0 4px", fontWeight: 700, color: "var(--accent)", fontSize: 14 }}>
          {Math.round(p.price).toLocaleString("ru-RU")} ₽
        </p>
        {rt && color && (
          <span className="pill" style={{ background: color.bg, color: color.text }}>
            {RESERVATION_LABELS[rt]}
          </span>
        )}
        {order.issued_at && (
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--hint)" }}>
            Выдан: {new Date(order.issued_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
          </p>
        )}
        {order.manager && (
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--hint)" }}>
            👤 Продавец: {order.manager.first_name ?? (order.manager.username ? `@${order.manager.username}` : `ID ${order.manager.id}`)}
          </p>
        )}
        {order.expires_at && order.status === "pending" && (
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--hint)" }}>
            До: {new Date(order.expires_at).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>
    </Link>
  );
}

function PriceChangeCard({ pc }: { pc: PriceChange }) {
  const p = pc.product;
  const drop = pc.new_price < pc.old_price;
  return (
    <Link to={`/product/${p.id}`} style={{ display: "flex", gap: 12, padding: 12, background: "var(--secondary-bg)", borderRadius: 14, marginBottom: 8 }}>
      <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={64} variant="thumb" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14 }}>
          {p.title ?? categoryLabel(p.category)}
        </p>
        <p className="micro" style={{ margin: "0 0 7px", color: "var(--hint)" }}>
          {p.size} · #{p.sku}
        </p>
        <p style={{ margin: "0 0 4px", fontSize: 14 }}>
          <span style={{ color: "var(--hint)", textDecoration: "line-through" }}>
            {Math.round(pc.old_price).toLocaleString("ru-RU")} ₽
          </span>
          {" → "}
          <span style={{ fontWeight: 700, color: drop ? "#1a7f1a" : "#b00020" }}>
            {Math.round(pc.new_price).toLocaleString("ru-RU")} ₽
          </span>
          {" "}
          <span style={{ fontSize: 12, color: drop ? "#1a7f1a" : "#b00020" }}>
            {drop ? "▼" : "▲"} {Math.abs(Math.round(pc.new_price - pc.old_price)).toLocaleString("ru-RU")} ₽
          </span>
        </p>
        <p style={{ margin: 0, fontSize: 12, color: "var(--hint)" }}>
          {pc.changed_by_name ?? (pc.changed_by_username ? `@${pc.changed_by_username}` : "менеджер")}
          {" · "}
          {new Date(pc.changed_at).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </Link>
  );
}

function FunnelView({ data }: { data: Funnel }) {
  const max = Math.max(data.views, 1);
  const stages = [
    { icon: "👁", label: "Просмотры", value: data.views, cr: 1, color: "#5b8def" },
    { icon: "❤️", label: "Лайки", value: data.likes, cr: data.cr_like, color: "#e0567a" },
    { icon: "🔒", label: "Брони", value: data.reservations, cr: data.cr_reservation, color: "#caa23a" },
    { icon: "💰", label: "Выкупы", value: data.purchases, cr: data.cr_purchase, color: "#2a9d57" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Воронка */}
      <div style={{ padding: "14px 16px", background: "var(--secondary-bg)", borderRadius: 14 }}>
        <p className="section-label" style={{ margin: "0 0 12px" }}>Воронка (за всё время)</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {stages.map((s) => (
            <div key={s.label}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span>{s.icon} {s.label}</span>
                <span style={{ color: "var(--hint)" }}>
                  <b style={{ color: "var(--text)" }}>{s.value.toLocaleString("ru-RU")}</b>
                  {s.cr < 1 && <> · {(s.cr * 100).toFixed(s.cr * 100 < 1 ? 2 : 1)}% от просмотров</>}
                </span>
              </div>
              <div style={{ height: 10, background: "var(--bg)", borderRadius: 6, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.max((s.value / max) * 100, s.value > 0 ? 2 : 0)}%`,
                  height: "100%", background: s.color, borderRadius: 6, transition: "width 0.4s",
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Смотрят, но не покупают */}
      {data.top_viewed_not_sold.length > 0 && (
        <div style={{ padding: "14px 16px", background: "var(--secondary-bg)", borderRadius: 14 }}>
          <p className="section-label" style={{ margin: "0 0 4px" }}>Смотрят, но не покупают</p>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--hint)" }}>
            Высокий интерес, но ещё в наличии — возможный сигнал цены.
          </p>
          {data.top_viewed_not_sold.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", color: "inherit" }}
            >
              <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={44} variant="thumb" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.title ?? CATEGORY_LABELS[p.category as keyof typeof CATEGORY_LABELS] ?? p.category}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "var(--hint)" }}>
                  {Math.round(p.price).toLocaleString("ru-RU")} ₽ · 👁 {p.views} · ❤️ {p.likes}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* По категориям */}
      {data.by_category.length > 0 && (
        <div style={{ padding: "14px 16px", background: "var(--secondary-bg)", borderRadius: 14 }}>
          <p className="section-label">По категориям</p>
          <div className="thead">
            <span style={{ flex: 1 }}>Категория</span>
            <span style={{ width: 56, textAlign: "right" }}>👁</span>
            <span style={{ width: 56, textAlign: "right" }}>❤️</span>
            <span style={{ width: 56, textAlign: "right" }}>💰</span>
          </div>
          {data.by_category.slice(0, 10).map((c) => (
            <div key={c.category} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
              <span style={{ flex: 1 }}>{CATEGORY_LABELS[c.category as keyof typeof CATEGORY_LABELS] ?? c.category}</span>
              <span style={{ width: 56, textAlign: "right" }}>{c.views}</span>
              <span style={{ width: 56, textAlign: "right" }}>{c.likes}</span>
              <span style={{ width: 56, textAlign: "right", fontWeight: 600 }}>{c.sold}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Пустая вкладка. Клякса — тот же приём, что в каталоге и заказах; здесь он
   вынесен в компонент, потому что пустой может оказаться любая из вкладок. */
function Empty({ text, variant = 1 }: { text: string; variant?: number }) {
  return (
    <div className="empty">
      <HandBlot variant={variant} className="empty__blot" />
      <p style={{ color: "var(--hint)", margin: 0 }}>{text}</p>
    </div>
  );
}

export default function OwnerPage() {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab]     = useState<"overview" | "funnel" | "sold" | "reservations" | "prices">("overview");

  const [report, setReport]   = useState<Awaited<ReturnType<typeof monthlyReport>> | null>(null);
  const [funnel, setFunnel]   = useState<Funnel | null>(null);
  const [sold, setSold]       = useState<Order[]>([]);
  const [reserves, setReserves] = useState<Order[]>([]);
  const [prices, setPrices]   = useState<PriceChange[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    monthlyReport(year, month).then(setReport).catch(() => setReport(null));
  }, [year, month]);

  useEffect(() => {
    if (tab === "funnel" && !funnel) {
      setLoading(true);
      conversionFunnel().then(setFunnel).catch(() => setFunnel(null)).finally(() => setLoading(false));
    } else if (tab === "sold") {
      setLoading(true);
      soldOrders(year, month).then(setSold).finally(() => setLoading(false));
    } else if (tab === "reservations") {
      setLoading(true);
      activeReservations().then(setReserves).finally(() => setLoading(false));
    } else if (tab === "prices") {
      setLoading(true);
      priceChanges(year, month).then(setPrices).finally(() => setLoading(false));
    }
  }, [tab, year, month]);

  return (
    <div className="container">
      <h1>Аналитика</h1>

      {/* Период */}
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="field">
          <label>Год</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </div>
        <div className="field">
          <label>Месяц</label>
          <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value))} />
        </div>
      </div>

      {/* Вкладки */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>
          Обзор
        </button>
        <button className={tab === "funnel" ? "active" : ""} onClick={() => setTab("funnel")}>
          Воронка
        </button>
        <button className={tab === "sold" ? "active" : ""} onClick={() => setTab("sold")}>
          Продажи{report ? ` (${report.sales_count})` : ""}
        </button>
        <button className={tab === "reservations" ? "active" : ""} onClick={() => setTab("reservations")}>
          Брони{reserves.length > 0 ? ` (${reserves.length})` : ""}
        </button>
        <button className={tab === "prices" ? "active" : ""} onClick={() => setTab("prices")}>
          Цены{prices.length > 0 ? ` (${prices.length})` : ""}
        </button>
      </div>

      {/* Обзор */}
      {tab === "overview" && (
        report ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              ["💰 Выручка", `${Math.round(report.revenue).toLocaleString("ru-RU")} ₽`],
              ["📦 Продано", String(report.sales_count)],
              ["🧾 Средний чек", `${Math.round(report.avg_check).toLocaleString("ru-RU")} ₽`],
              ["🔒 Броней оформлено", String(report.reservations_count)],
              ["✅ Конверсия бронь→выкуп", `${Math.round(report.reservation_conversion * 100)}%`],
              ["🔥 Броней сгорело", String(report.reservations_burned)],
              ["💸 Доход от сгоревших залогов", `${Math.round(report.deposits_burned_revenue).toLocaleString("ru-RU")} ₽`],
              ["➕ Добавлено вещей", String(report.products_added)],
              [`🐌 Висяки (>${report.stale_days} дн)`, String(report.stale_count)],
            ].map(([label, value]) => (
              <div key={label} className="stat">
                <span className="stat__label">{label}</span>
                <span className="stat__value">{value}</span>
              </div>
            ))}

            {report.top_categories.length > 0 && (
              <div style={{ padding: "12px 16px", background: "var(--secondary-bg)", borderRadius: 14 }}>
                <p className="section-label">Топ категорий</p>
                {report.top_categories.map((c, i) => (
                  <div key={c.category} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ color: "var(--hint)", fontSize: 14 }}>{i + 1}. {CATEGORY_LABELS[c.category as keyof typeof CATEGORY_LABELS] ?? c.category}</span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{c.count} шт.</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : <Empty text="Нет данных." />
      )}

      {/* Воронка */}
      {tab === "funnel" && (
        loading ? <p style={{ color: "var(--hint)" }}>Загрузка…</p>
        : funnel ? <FunnelView data={funnel} />
        : <Empty text="Нет данных." />
      )}

      {/* Продажи */}
      {tab === "sold" && (
        loading ? <p style={{ color: "var(--hint)" }}>Загрузка…</p>
        : sold.length === 0 ? <Empty text="Продаж за этот период нет." variant={2} />
        : sold.map((o) => <ProductOrderCard key={o.id} order={o} />)
      )}

      {/* Брони */}
      {tab === "reservations" && (
        loading ? <p style={{ color: "var(--hint)" }}>Загрузка…</p>
        : reserves.length === 0 ? <Empty text="Активных броней нет." variant={3} />
        : reserves.map((o) => <ProductOrderCard key={o.id} order={o} />)
      )}

      {/* История цен */}
      {tab === "prices" && (
        loading ? <p style={{ color: "var(--hint)" }}>Загрузка…</p>
        : prices.length === 0 ? <Empty text="Изменений цен за этот период нет." variant={2} />
        : prices.map((pc) => <PriceChangeCard key={pc.id} pc={pc} />)
      )}
    </div>
  );
}
